import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import Select from "react-select";
import CreatableSelect from "react-select/creatable";
import { useFormik } from "formik";
import * as Yup from "yup";

import { AppDispatch } from "../../store";
import { showAlertWithTimeout } from "../../slices/Alert/reducer";
import { CreateMenuRequest, createMenu } from "../../slices/Web/menuCreate/thunk";
import { getMenuCategories, getMenuOptions } from "../../slices/thunks";
import { initialState as menuOptionsInitialState } from "../../slices/MenuOptions/reducer";
import {
    buildMenuOptionChoices,
    getMenuOptionIsActive,
    getMenuOptionName,
    getMenuOptionSelectionType,
    toComparableMenuOptionId,
} from "../../helpers/menuOptions";
import { getMenuOptionValues } from "../../helpers/backend_helper";
import HeaderSidebarToggle from "../../Components/Common/HeaderSidebarToggle";

interface MenuFormValues {
    name: string;
    price: string;
    description: string;
    image: string;
    categories: string[];
    optionSelections: string[];
}

interface OptionValueItem {
    id: string | number;
    label: string;
    isAvailable: boolean;
    priceModifier: string;
    isDefault: boolean;
}

interface OptionValueGroup {
    optionId: string | number;
    name: string;
    selectionType: "single" | "multiple";
    values: OptionValueItem[];
}

const extractOptionValues = (payload: any): any[] => {
    if (!payload) {
        return [];
    }

    const data = payload?.data ?? payload;
    if (Array.isArray(data)) {
        return data;
    }

    if (Array.isArray(data?.data)) {
        return data.data;
    }

    if (Array.isArray(data?.option_values)) {
        return data.option_values;
    }

    return [];
};

const validationSchema = Yup.object({
    name: Yup.string().trim().required("Vui lòng nhập tên món"),
    price: Yup.number()
        .typeError("Giá phải là số hợp lệ")
        .positive("Giá phải lớn hơn 0")
        .required("Vui lòng nhập giá"),
    description: Yup.string().trim(),
    image: Yup.string()
        .trim()
        .transform((value) => (value === "" ? undefined : value))
        .url("Đường dẫn ảnh không hợp lệ")
        .notRequired()
        .nullable(),
    categories: Yup.array()
        .of(Yup.string().trim())
        .min(1, "Vui lòng nhập ít nhất 1 danh mục")
        .required("Vui lòng nhập ít nhất 1 danh mục"),
    optionSelections: Yup.array().of(Yup.string().trim()),
});

const AddMenu = () => {
    const dispatch = useDispatch<AppDispatch>();
    const navigate = useNavigate();
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [optionValueGroups, setOptionValueGroups] = useState<
        Record<string, OptionValueGroup>
    >({});
    const [optionValuesLoading, setOptionValuesLoading] = useState(false);
    const menuOptionsState = useSelector(
        (state: any) => state.MenuOptions ?? menuOptionsInitialState
    );
    const menuListState = useSelector((state: any) => state.MenuList);
    const categoriesList = useMemo(
        () => (Array.isArray(menuListState?.categories) ? menuListState.categories : []),
        [menuListState?.categories]
    );

    const optionList = useMemo(
        () => (Array.isArray(menuOptionsState.options) ? menuOptionsState.options : []),
        [menuOptionsState.options]
    );

    useEffect(() => {
        if (!menuOptionsState.loading && !menuOptionsState.loaded) {
            dispatch(getMenuOptions());
        }
    }, [dispatch, menuOptionsState.loading, menuOptionsState.loaded]);

    useEffect(() => {
        if (categoriesList.length === 0) {
            dispatch(getMenuCategories());
        }
    }, [dispatch, categoriesList.length]);

    const optionChoices = useMemo(
        () =>
            buildMenuOptionChoices(optionList).filter((choice) =>
                getMenuOptionIsActive(choice.option)
            ),
        [optionList]
    );

    const choiceLookup = useMemo(() => {
        const map = new Map<string, (typeof optionChoices)[number]>();
        optionChoices.forEach((choice) => {
            map.set(choice.value, choice);
        });
        return map;
    }, [optionChoices]);

    const groupedSelectOptions = useMemo(() => {
        type SelectOption = { value: string; label: string };
        const groups = new Map<string, SelectOption[]>();
        const ungrouped: SelectOption[] = [];

        optionChoices.forEach((choice) => {
            const selectOption = { value: choice.value, label: choice.label };
            if (choice.group) {
                if (!groups.has(choice.group)) {
                    groups.set(choice.group, []);
                }
                groups.get(choice.group)!.push(selectOption);
            } else {
                ungrouped.push(selectOption);
            }
        });

        if (groups.size === 0) {
            return ungrouped;
        }

        const grouped = Array.from(groups.entries())
            .sort(([a], [b]) => a.localeCompare(b))
            .map(([label, options]) => ({ label, options }));

        if (ungrouped.length > 0) {
            grouped.push({ label: "Khác", options: ungrouped });
        }

        return grouped;
    }, [optionChoices]);

    const formik = useFormik<MenuFormValues>({
        initialValues: {
            name: "",
            price: "",
            description: "",
            image: "",
            categories: [],
            optionSelections: [],
        },
        validationSchema,
        onSubmit: async (values) => {
            setIsSubmitting(true);
            const normalizedCategories = Array.from(
                new Set(values.categories.map((value) => value.trim()).filter(Boolean))
            );
            const payload: CreateMenuRequest = {
                name: values.name.trim(),
                price: Number(values.price),
                description: values.description.trim() || undefined,
                image: values.image.trim() || undefined,
                categories: normalizedCategories,
            };

            const selectedChoices = values.optionSelections
                .map((value) => choiceLookup.get(value))
                .filter(Boolean) as (typeof optionChoices)[number][];

            const optionIds: (string | number)[] = [];
            const optionPayloads: any[] = [];
            const seen = new Set<string>();

            selectedChoices.forEach((choice) => {
                const comparable = choice.value;
                if (seen.has(comparable)) {
                    return;
                }
                seen.add(comparable);
                optionIds.push(choice.rawId ?? choice.value);
                optionPayloads.push(choice.option);
            });

            if (optionIds.length > 0) {
                payload.optionIds = optionIds;
                payload.options = optionPayloads;
                const optionValuesPayload: Array<{
                    option_value_id: string | number;
                    price_modifier: number;
                    is_default: boolean;
                }> = [];

                optionIds.forEach((optionId) => {
                    const comparable = toComparableMenuOptionId(optionId);
                    if (!comparable) {
                        return;
                    }
                    const group = optionValueGroups[comparable];
                    if (!group) {
                        return;
                    }
                    group.values.forEach((value) => {
                        const price = Number(value.priceModifier);
                        optionValuesPayload.push({
                            option_value_id: value.id,
                            price_modifier: Number.isFinite(price) ? price : 0,
                            is_default: group.selectionType === "single" ? value.isDefault : false,
                        });
                    });
                });

                if (optionValuesPayload.length > 0) {
                    payload.menu_option_values = optionValuesPayload;
                }
            }

            try {
                await dispatch(createMenu(payload)).unwrap();
                dispatch(
                    showAlertWithTimeout(
                        "success",
                        "Tạo món thành công",
                        "Món mới đã được thêm vào danh sách."
                    )
                );
                navigate("/menu");
            } catch (error) {
                const errorMessage =
                    typeof error === "string"
                        ? error
                        : "Không thể tạo món vào lúc này. Vui lòng thử lại.";
                dispatch(
                    showAlertWithTimeout(
                        "danger",
                        "Tạo món thất bại",
                        errorMessage
                    )
                );
            } finally {
                setIsSubmitting(false);
            }
        },
    });

    const selectedChoices = useMemo(
        () =>
            formik.values.optionSelections
                .map((value) => choiceLookup.get(value))
                .filter(Boolean) as (typeof optionChoices)[number][],
        [choiceLookup, formik.values.optionSelections]
    );

    const selectedSelectValues = useMemo(
        () =>
            selectedChoices.map((choice) => ({
                value: choice.value,
                label: choice.label,
            })),
        [selectedChoices]
    );

    const buildOptionValueGroup = useCallback(
        (choice: (typeof optionChoices)[number], values: any[]): OptionValueGroup => {
            const option = choice.option;
            const selectionType = getMenuOptionSelectionType(option);
            const name = getMenuOptionName(option) || choice.label;

            const mappedValues: OptionValueItem[] = values.map((value: any) => {
                const label =
                    value?.label ?? value?.name ?? value?.title ?? value?.displayName ?? "Lựa chọn";
                const rawAvailability =
                    value?.is_available ?? value?.isAvailable ?? value?.available ?? true;
                const availability =
                    typeof rawAvailability === "boolean"
                        ? rawAvailability
                        : typeof rawAvailability === "number"
                          ? rawAvailability !== 0
                          : String(rawAvailability).toLowerCase() !== "false";
                const price =
                    value?.price_modifier ?? value?.priceModifier ?? value?.price ?? 0;
                return {
                    id: value?.id ?? value?.option_value_id ?? value?.value_id,
                    label: String(label),
                    isAvailable: availability,
                    priceModifier: String(price ?? 0),
                    isDefault: Boolean(value?.is_default ?? value?.isDefault ?? false),
                };
            });

            if (selectionType === "single" && mappedValues.length > 0) {
                const hasDefault = mappedValues.some((item) => item.isDefault);
                if (!hasDefault) {
                    mappedValues[0].isDefault = true;
                }
            }

            return {
                optionId: choice.rawId ?? choice.value,
                name,
                selectionType,
                values: mappedValues,
            };
        },
        [optionChoices]
    );

    const optionValueGroupsRef = useRef(optionValueGroups);

    useEffect(() => {
        optionValueGroupsRef.current = optionValueGroups;
    }, [optionValueGroups]);

    useEffect(() => {
        const selected = formik.values.optionSelections
            .map((value) => choiceLookup.get(value))
            .filter(Boolean) as (typeof optionChoices)[number][];
        const selectedSet = new Set(selected.map((choice) => choice.value));

        setOptionValueGroups((prev) => {
            const next = { ...prev };
            let didChange = false;
            Object.keys(next).forEach((key) => {
                if (!selectedSet.has(key)) {
                    delete next[key];
                    didChange = true;
                }
            });
            return didChange ? next : prev;
        });

        const currentGroups = optionValueGroupsRef.current;
        const missing = selected.filter((choice) => !currentGroups[choice.value]);
        if (missing.length === 0) {
            return;
        }

        let isMounted = true;
        setOptionValuesLoading(true);

        Promise.all(
            missing.map(async (choice) => {
                const optionId = choice.rawId ?? choice.value;
                const response = await getMenuOptionValues(optionId);
                const values = extractOptionValues(response?.data);
                return { key: choice.value, group: buildOptionValueGroup(choice, values) };
            })
        )
            .then((results) => {
                if (!isMounted) {
                    return;
                }
                setOptionValueGroups((prev) => {
                    const next = { ...prev };
                    results.forEach((result) => {
                        if (result?.group) {
                            next[result.key] = result.group;
                        }
                    });
                    return next;
                });
            })
            .catch((fetchError) => {
                console.error(fetchError);
            })
            .finally(() => {
                if (isMounted) {
                    setOptionValuesLoading(false);
                }
            });

        return () => {
            isMounted = false;
        };
    }, [formik.values.optionSelections, choiceLookup, buildOptionValueGroup]);

    const handlePriceModifierChange = (
        optionKey: string,
        valueId: string | number,
        nextValue: string
    ) => {
        setOptionValueGroups((prev) => {
            const group = prev[optionKey];
            if (!group) {
                return prev;
            }

            const updatedValues = group.values.map((value) =>
                String(value.id) === String(valueId)
                    ? { ...value, priceModifier: nextValue }
                    : value
            );

            return {
                ...prev,
                [optionKey]: { ...group, values: updatedValues },
            };
        });
    };

    const handleDefaultChange = (optionKey: string, valueId: string | number) => {
        setOptionValueGroups((prev) => {
            const group = prev[optionKey];
            if (!group) {
                return prev;
            }

            const updatedValues = group.values.map((value) => ({
                ...value,
                isDefault: String(value.id) === String(valueId),
            }));

            return {
                ...prev,
                [optionKey]: { ...group, values: updatedValues },
            };
        });
    };

    const optionGroupList = useMemo(() => {
        return Object.values(optionValueGroups).sort((a, b) =>
            a.name.localeCompare(b.name)
        );
    }, [optionValueGroups]);

    const categoryOptions = useMemo(() => {
        const unique = new Map<string, string>();
        categoriesList.forEach((category: any) => {
            const name = category?.name ?? category?.title ?? category?.label ?? category?.displayName;
            if (typeof name === "string" && name.trim().length > 0) {
                unique.set(name.trim(), name.trim());
            }
        });
        return Array.from(unique.values()).map((name) => ({
            value: name,
            label: name,
        }));
    }, [categoriesList]);

    const selectedCategoryValues = useMemo(
        () =>
            formik.values.categories.map((name) => ({
                value: name,
                label: name,
            })),
        [formik.values.categories]
    );

    return (
        <React.Fragment>
            <div className="header-area" id="headerArea">
                <div className="container h-100 d-flex text-center align-items-center justify-content-between rtl-flex-d-row-r">
                    <div className="back-button me-2">
                        <Link to="/menu">
                            <i className="mdi mdi-arrow-left"></i>
                        </Link>
                    </div>

                    <div className="page-heading">
                        <h6 className="mb-0">Tạo mới món</h6>
                    </div>

                    <HeaderSidebarToggle className="ms-2" />
                </div>
            </div>

            <div className="page-content-wrapper">
                <div className="container">
                    <div className="profile-wrapper-area py-3">
                        <div className="card user-data-card">
                            <div className="card-body">
                                <form onSubmit={formik.handleSubmit} noValidate>
                                    <div className="mb-3">
                                        <div className="title mb-2">
                                            <i className="mdi mdi-notebook-outline"></i>
                                            <span>Tên món</span>
                                        </div>
                                        <input
                                            className={`form-control ${
                                                formik.touched.name && formik.errors.name
                                                    ? "is-invalid"
                                                    : ""
                                            }`}
                                            type="text"
                                            name="name"
                                            value={formik.values.name}
                                            onChange={formik.handleChange}
                                            onBlur={formik.handleBlur}
                                            placeholder="Nhập tên món"
                                        />
                                        {formik.touched.name && formik.errors.name && (
                                            <div className="invalid-feedback d-block">
                                                {formik.errors.name}
                                            </div>
                                        )}
                                    </div>

                                    <div className="mb-3">
                                        <div className="title mb-2">
                                            <i className="mdi mdi-currency-usd"></i>
                                            <span>Giá</span>
                                        </div>
                                        <input
                                            className={`form-control ${
                                                formik.touched.price && formik.errors.price
                                                    ? "is-invalid"
                                                    : ""
                                            }`}
                                            type="number"
                                            name="price"
                                            min="0"
                                            step="1000"
                                            value={formik.values.price}
                                            onChange={formik.handleChange}
                                            onBlur={formik.handleBlur}
                                            placeholder="Nhập giá món"
                                        />
                                        {formik.touched.price && formik.errors.price && (
                                            <div className="invalid-feedback d-block">
                                                {formik.errors.price}
                                            </div>
                                        )}
                                    </div>

                                    <div className="mb-3">
                                        <div className="title mb-2">
                                            <i className="mdi mdi-shape-outline"></i>
                                            <span>Danh mục</span>
                                        </div>
                                        <CreatableSelect
                                            isMulti
                                            closeMenuOnSelect={false}
                                            options={categoryOptions}
                                            value={selectedCategoryValues}
                                            classNamePrefix="react-select"
                                            placeholder="Chọn hoặc nhập danh mục"
                                            onChange={(selected) => {
                                                const selections = Array.isArray(selected)
                                                    ? selected.map((item: any) => item.value)
                                                    : [];
                                                formik.setFieldValue("categories", selections);
                                            }}
                                            onBlur={() => formik.setFieldTouched("categories", true)}
                                            isDisabled={isSubmitting}
                                        />
                                        {formik.touched.categories && formik.errors.categories && (
                                            <div className="invalid-feedback d-block">
                                                {formik.errors.categories as string}
                                            </div>
                                        )}
                                    </div>

                                    <div className="mb-3">
                                        <div className="title mb-2">
                                            <i className="mdi mdi-image-outline"></i>
                                            <span>Ảnh (URL)</span>
                                        </div>
                                        <input
                                            className={`form-control ${
                                                formik.touched.image && formik.errors.image
                                                    ? "is-invalid"
                                                    : ""
                                            }`}
                                            type="url"
                                            name="image"
                                            value={formik.values.image}
                                            onChange={formik.handleChange}
                                            onBlur={formik.handleBlur}
                                            placeholder="https://example.com/mon-ngon.jpg"
                                        />
                                        {formik.touched.image && formik.errors.image && (
                                            <div className="invalid-feedback d-block">
                                                {formik.errors.image}
                                            </div>
                                        )}
                                    </div>

                                    <div className="mb-3">
                                        <div className="title mb-2">
                                            <i className="mdi mdi-format-align-left"></i>
                                            <span>Mô tả</span>
                                        </div>
                                        <textarea
                                            className="form-control"
                                            rows={3}
                                            name="description"
                                            value={formik.values.description}
                                            onChange={formik.handleChange}
                                            onBlur={formik.handleBlur}
                                            placeholder="Mô tả ngắn về món"
                                        ></textarea>
                                    </div>

                                    <div className="mb-3">
                                        <div className="title mb-2">
                                            <i className="mdi mdi-playlist-check"></i>
                                            <span>Tùy chọn áp dụng</span>
                                        </div>
                                        <Select
                                            isMulti
                                            closeMenuOnSelect={false}
                                            options={groupedSelectOptions as any}
                                            value={selectedSelectValues as any}
                                            isLoading={menuOptionsState.loading}
                                            classNamePrefix="react-select"
                                            placeholder="Chọn tùy chọn áp dụng cho món"
                                            onChange={(selected) => {
                                                const selections = Array.isArray(selected)
                                                    ? selected.map((item: any) => item.value)
                                                    : [];
                                                formik.setFieldValue("optionSelections", selections);
                                            }}
                                            onBlur={() => formik.setFieldTouched("optionSelections", true)}
                                            isDisabled={isSubmitting}
                                        />
                                        {menuOptionsState.error && (
                                            <p className="text-danger small mt-2">
                                                {menuOptionsState.error}
                                            </p>
                                        )}
                                        {selectedSelectValues.length > 0 && (
                                            <div className="mt-2">
                                                {selectedSelectValues.map((item) => (
                                                    <span
                                                        key={item.value}
                                                        className="badge bg-primary-subtle text-primary me-1 mb-1"
                                                    >
                                                        {item.label}
                                                    </span>
                                                ))}
                                            </div>
                                        )}
                                    </div>

                                    {selectedSelectValues.length > 0 && (
                                        <div className="mb-3">
                                            <div className="title mb-2">
                                                <i className="mdi mdi-format-list-bulleted"></i>
                                                <span>Cấu hình lựa chọn</span>
                                            </div>
                                            {optionValuesLoading && (
                                                <div className="text-muted small mb-2">
                                                    Đang tải lựa chọn...
                                                </div>
                                            )}
                                            {optionGroupList.length === 0 && !optionValuesLoading && (
                                                <div className="alert alert-warning">
                                                    Chưa có lựa chọn nào cho các tùy chọn đã chọn.
                                                </div>
                                            )}
                                            {optionGroupList.map((group) => (
                                                <div key={group.optionId} className="card mb-2">
                                                    <div className="card-body p-2">
                                                        <div className="d-flex align-items-center justify-content-between mb-2">
                                                            <div className="fw-semibold">{group.name}</div>
                                                            <span className="badge bg-info-subtle text-info">
                                                                {group.selectionType === "single"
                                                                    ? "Chọn 1"
                                                                    : "Chọn nhiều"}
                                                            </span>
                                                        </div>
                                                        {group.values.length === 0 ? (
                                                            <div className="text-muted small">
                                                                Chưa có lựa chọn nào.
                                                            </div>
                                                        ) : (
                                                            <div className="table-responsive">
                                                                <table className="table table-sm mb-0">
                                                                    <thead>
                                                                        <tr>
                                                                            <th>Lựa chọn</th>
                                                                            <th className="text-end">Giá cộng thêm</th>
                                                                            {group.selectionType === "single" && (
                                                                                <th className="text-center">
                                                                                    Mặc định
                                                                                </th>
                                                                            )}
                                                                        </tr>
                                                                    </thead>
                                                                    <tbody>
                                                                        {group.values.map((value) => (
                                                                            <tr key={value.id}>
                                                                                <td>
                                                                                    <span className="fw-medium">
                                                                                        {value.label}
                                                                                    </span>
                                                                                    {!value.isAvailable && (
                                                                                        <span className="badge bg-secondary-subtle text-secondary ms-2">
                                                                                            Hết
                                                                                        </span>
                                                                                    )}
                                                                                </td>
                                                                                <td className="text-end">
                                                                                    <input
                                                                                        type="number"
                                                                                        step="0.01"
                                                                                        className="form-control form-control-sm text-end"
                                                                                        value={value.priceModifier}
                                                                                        onChange={(event) =>
                                                                                            handlePriceModifierChange(
                                                                                                toComparableMenuOptionId(
                                                                                                    group.optionId
                                                                                                ) ?? String(group.optionId),
                                                                                                value.id,
                                                                                                event.target.value
                                                                                            )
                                                                                        }
                                                                                    />
                                                                                </td>
                                                                                {group.selectionType === "single" && (
                                                                                    <td className="text-center">
                                                                                        <input
                                                                                            type="radio"
                                                                                            name={`default-${group.optionId}`}
                                                                                            checked={value.isDefault}
                                                                                            onChange={() =>
                                                                                                handleDefaultChange(
                                                                                                    toComparableMenuOptionId(
                                                                                                        group.optionId
                                                                                                    ) ?? String(group.optionId),
                                                                                                    value.id
                                                                                                )
                                                                                            }
                                                                                        />
                                                                                    </td>
                                                                                )}
                                                                            </tr>
                                                                        ))}
                                                                    </tbody>
                                                                </table>
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    )}

                                    <button
                                        className="btn btn-primary btn-lg w-100"
                                        type="submit"
                                        disabled={isSubmitting}
                                    >
                                        {isSubmitting ? "Đang lưu..." : "Tạo món"}
                                    </button>
                                </form>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </React.Fragment>
    );
};

export default AddMenu;
